import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import BottomNav from '../../components/common/BottomNav';
import DoctorCard from '../../components/common/DoctorCard';
import { SkeletonCard } from '../../components/common/Loader';
import { searchDoctors, getSearchSuggestions } from '../../services/patientService';
import { DISEASES } from '../../utils/constants';

// ── Suggestion item ────────────────────────────────────────────────────────
function SuggestionItem({ s, onSelect }) {
  return (
    <button
      onMouseDown={e => { e.preventDefault(); onSelect(s); }}
      className="flex items-center gap-3 w-full px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left border-b border-slate-100 last:border-0">
      <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>
        <i className={`fa-solid ${s.icon} ${s.color} text-sm`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">{s.label}</p>
        {s.sub && <p className="text-xs text-text-muted truncate">{s.sub}</p>}
      </div>
      <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 ${
        s.type === 'doctor'   ? 'bg-primary-light text-primary'   :
        s.type === 'clinic'   ? 'bg-blue-50 text-blue-600'        :
        s.type === 'store'    ? 'bg-green-50 text-green-600'      :
        'bg-red-50 text-red-500'
      }`}>
        {s.type === 'doctor' ? 'Doctor' : s.type === 'clinic' ? 'Clinic' : s.type === 'store' ? 'Store' : 'Area'}
      </span>
    </button>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery]           = useState(searchParams.get('q') || '');
  const [location, setLocation]     = useState(searchParams.get('location') || '');
  const [locating, setLocating]     = useState(false);
  const [doctors, setDoctors]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(false);
  const [total, setTotal]           = useState(0);

  // Suggestions state
  const [suggestions, setSuggestions]     = useState([]);
  const [showSuggest, setShowSuggest]     = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeInput, setActiveInput]     = useState(null); // 'query' | 'location'
  const suggestTimer = useRef(null);
  const queryInputRef = useRef(null);

  const speciality = searchParams.get('speciality') || '';

  // ── Fetch suggestions with debounce ─────────────────────────────────────
  const fetchSuggestions = useCallback(async (val) => {
    if (!val || val.trim().length < 1) { setSuggestions([]); return; }
    setSuggestLoading(true);
    try {
      const res = await getSearchSuggestions(val.trim());
      setSuggestions(res.suggestions || []);
    } catch { setSuggestions([]); }
    finally { setSuggestLoading(false); }
  }, []);

  const handleQueryChange = (val) => {
    setQuery(val);
    clearTimeout(suggestTimer.current);
    if (val.trim().length >= 1) {
      setShowSuggest(true);
      suggestTimer.current = setTimeout(() => fetchSuggestions(val), 250);
    } else {
      setShowSuggest(false);
      setSuggestions([]);
    }
  };

  const handleLocationChange = (val) => {
    setLocation(val);
    clearTimeout(suggestTimer.current);
    if (val.trim().length >= 1) {
      setShowSuggest(true);
      // For location input: filter only location-type suggestions
      suggestTimer.current = setTimeout(async () => {
        setSuggestLoading(true);
        try {
          const res = await getSearchSuggestions(val.trim());
          // Also add all types to let user pick area/city
          setSuggestions(res.suggestions || []);
        } catch {}
        finally { setSuggestLoading(false); }
      }, 250);
    } else {
      setShowSuggest(false);
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (s) => {
    setShowSuggest(false);
    setSuggestions([]);

    if (s.doctorId && s.type !== 'location') {
      // Direct: go to doctor profile
      if (s.type === 'store') {
        navigate(`/medicine-chat/${s.doctorId}`, { state: { staffName: s.label } });
        return;
      }
      navigate(`/doctor/${s.doctorId}`);
      return;
    }

    if (s.type === 'location') {
      setLocation(s.query);
      doSearch(query, s.query, speciality, 1);
      return;
    }

    setQuery(s.query);
    doSearch(s.query, location, speciality, 1);
  };

  // ── Main search ──────────────────────────────────────────────────────────
  const doSearch = useCallback(async (q, loc, spec, pg = 1) => {
    setLoading(true);
    setShowSuggest(false);
    try {
      const params = { page: pg, limit: 10 };
      if (q)    params.q          = q;
      if (loc)  params.location   = loc;
      if (spec) params.speciality = spec;
      const res = await searchDoctors(params);
      if (pg === 1) setDoctors(res.doctors || []);
      else setDoctors(prev => [...prev, ...(res.doctors || [])]);
      setTotal(res.total || 0);
      setHasMore(pg < (res.pages || 1));
      setPage(pg);
    } catch { setDoctors([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    doSearch(searchParams.get('q'), location, speciality, 1);
  }, [searchParams, speciality, doSearch, location]);

  // ── Live location ────────────────────────────────────────────────────────
  const getLiveLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        try {
          const { latitude: lat, longitude: lng } = pos.coords;
          const res  = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || 'Your Location';
          setLocation(city);
          toast.success(`Location: ${city}`);
          doSearch(query, city, speciality, 1);
        } catch { toast.error('Could not detect location'); }
        finally { setLocating(false); }
      },
      () => { toast.error('Please allow location access'); setLocating(false); }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    doSearch(query, location, speciality, 1);
  };

  return (
    <div className="page-container">
      <Header showBack title="Find Doctors" />

      {/* Sticky search + suggestions */}
      <div className="sticky top-14 z-20 bg-white border-b border-border">
        <form onSubmit={handleSearch} className="px-4 pt-3 pb-2 space-y-2">

          {/* Query input */}
          <div className="relative">
            <div className={`flex items-center gap-2 bg-surface border rounded-2xl px-4 py-3 transition-all ${
              activeInput === 'query' ? 'border-primary ring-2 ring-primary/15' : 'border-border'
            }`}>
              <i className="fa-solid fa-magnifying-glass text-primary text-sm flex-shrink-0" />
              <input
                ref={queryInputRef}
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                onFocus={() => { setActiveInput('query'); if (query.length >= 1) setShowSuggest(true); }}
                onBlur={() => { setActiveInput(null); setTimeout(() => setShowSuggest(false), 150); }}
                placeholder="Doctor, clinic, speciality, medicine store..."
                className="flex-1 bg-transparent outline-none text-sm text-text-primary placeholder:text-slate-400 min-w-0"
                autoComplete="off"
              />
              {query && (
                <button type="button" onClick={() => { setQuery(''); setSuggestions([]); setShowSuggest(false); }}>
                  <i className="fa-solid fa-xmark text-slate-400 text-sm" />
                </button>
              )}
              {suggestLoading && activeInput === 'query' && (
                <i className="fa-solid fa-spinner animate-spin text-primary text-xs flex-shrink-0" />
              )}
            </div>

            {/* Dropdown suggestions */}
            {showSuggest && activeInput === 'query' && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-xl border border-border overflow-hidden z-50">
                {/* Group header if mixed types */}
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex gap-1">
                    {[...new Set(suggestions.map(s => s.type))].map(type => (
                      <span key={type} className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full capitalize font-semibold">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
                {suggestions.map((s, i) => (
                  <SuggestionItem key={i} s={s} onSelect={handleSuggestionSelect} />
                ))}
              </div>
            )}
          </div>

          {/* Location input */}
          <div className="relative">
            <div className={`flex items-center gap-2 bg-surface border rounded-2xl px-3 py-2.5 transition-all ${
              activeInput === 'location' ? 'border-primary ring-2 ring-primary/15' : 'border-border'
            }`}>
              <i className="fa-solid fa-location-dot text-red-500 text-sm flex-shrink-0" />
              <input
                value={location}
                onChange={e => handleLocationChange(e.target.value)}
                onFocus={() => { setActiveInput('location'); if (location.length >= 1) setShowSuggest(true); }}
                onBlur={() => { setActiveInput(null); setTimeout(() => setShowSuggest(false), 150); }}
                placeholder="City, area or address (optional)"
                className="flex-1 bg-transparent outline-none text-sm text-text-primary placeholder:text-slate-400 min-w-0"
                autoComplete="off"
              />
              {location && (
                <button type="button" onClick={() => { setLocation(''); setSuggestions([]); }}>
                  <i className="fa-solid fa-xmark text-slate-400 text-xs" />
                </button>
              )}
              <button type="button" onClick={getLiveLocation} disabled={locating}
                className="flex-shrink-0 w-8 h-8 bg-primary rounded-xl flex items-center justify-center ml-1 active:scale-95 transition-transform">
                {locating
                  ? <i className="fa-solid fa-spinner animate-spin text-white text-xs" />
                  : <i className="fa-solid fa-crosshairs text-white text-sm" />}
              </button>
            </div>

            {/* Location suggestions dropdown */}
            {showSuggest && activeInput === 'location' && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-xl border border-border overflow-hidden z-50 max-h-52 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <SuggestionItem key={i} s={s} onSelect={handleSuggestionSelect} />
                ))}
              </div>
            )}
          </div>

          <button type="submit"
            className="w-full py-3 rounded-2xl bg-primary text-white font-bold text-sm shadow-md shadow-primary/20 active:scale-98 transition-all">
            <i className="fa-solid fa-magnifying-glass mr-2" />Search Doctors
          </button>
        </form>

        {/* Active filters */}
        {(speciality || location) && (
          <div className="flex items-center gap-2 flex-wrap px-4 pb-2">
            {speciality && (
              <span className="flex items-center gap-1.5 bg-primary-light text-primary text-xs font-bold px-3 py-1 rounded-full">
                <i className="fa-solid fa-stethoscope text-[10px]" />{speciality}
                <button onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.delete('speciality'); return n; })}>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              </span>
            )}
            {location && (
              <span className="flex items-center gap-1.5 bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full">
                <i className="fa-solid fa-location-dot text-[10px]" />{location}
                <button onClick={() => { setLocation(''); doSearch(query, '', speciality, 1); }}>
                  <i className="fa-solid fa-xmark text-[10px]" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Result count */}
      {!loading && (
        <div className="px-4 pt-3 pb-1 flex items-center justify-between">
          <p className="text-xs text-text-muted">
            {total > 0
              ? `${total} doctor${total !== 1 ? 's' : ''} found${speciality ? ` for "${speciality}"` : ''}${location ? ` in ${location}` : ''}`
              : query || location || speciality ? 'No doctors found' : ''}
          </p>
          {speciality && total > 0 && (
            <p className="text-[10px] text-primary font-medium">
              <i className="fa-solid fa-sort-amount-down mr-1" />Best match
            </p>
          )}
        </div>
      )}

      {/* Speciality chips when no search */}
      {!speciality && !query && !location && (
        <div className="px-4 pt-2 pb-1">
          <p className="text-xs font-semibold text-text-muted mb-2">Browse by speciality</p>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {DISEASES.map(d => (
              <button key={d.label}
                onClick={() => navigate(`/search?speciality=${encodeURIComponent(d.label)}`)}
                className="flex-shrink-0 px-3 py-1.5 bg-primary-light text-primary text-xs font-semibold rounded-full border border-primary/20 active:scale-95 transition-transform">
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="px-4 pt-2 pb-24 space-y-3">
        {loading && page === 1 ? (
          Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />)
        ) : doctors.length === 0 ? (
          <div className="flex flex-col items-center py-16 gap-4 text-center">
            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center">
              <i className="fa-solid fa-user-doctor text-text-muted text-2xl" />
            </div>
            <div>
              <p className="font-semibold text-text-primary">No doctors found</p>
              <p className="text-sm text-text-muted mt-1 max-w-xs">
                {speciality ? `No ${speciality} doctors verified yet` : 'Try different keywords or remove filters'}
              </p>
            </div>
            <button onClick={() => { setQuery(''); setLocation(''); setSearchParams({}); doSearch('', '', '', 1); }}
              className="px-6 py-2.5 border-2 border-primary text-primary rounded-2xl text-sm font-bold">
              Show All Doctors
            </button>
          </div>
        ) : (
          <>
            {doctors.map(doc => <DoctorCard key={doc._id} doctor={doc} />)}
            {hasMore && (
              <button onClick={() => doSearch(query, location, speciality, page + 1)} disabled={loading}
                className="w-full py-3 text-sm text-primary font-bold border-2 border-primary/30 rounded-2xl">
                {loading
                  ? <i className="fa-solid fa-spinner animate-spin" />
                  : `Load More (${total - doctors.length} more)`}
              </button>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
